"use client"
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import LoadingAnimation from "@/components/shared/LoadingAnimation";
import { Ratio, Tab } from "react-bootstrap";
import UralityImage from "@/components/shared/UralityImage";
import CollapseText from "@/components/shared/CollapseText";
import RichTextBlock from "@/components/shared/RichTextBlock";
import SocialLinks from "@/components/social/SocialLinks";

import "@/components/events/EventParticipantGroupParticipantList.css";
import moment from "moment";
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";
import { DateObject } from "react-multi-date-picker";
import useApi from "@/components/api/useApi";
import { useMediaQuery } from "react-responsive";
import FullscreenMapAndDrawer from "@/components/shared/FullscreenMapAndDrawer";
import MapBoxMapPopup from "@/components/shared/MapBoxMapPopup";

import MapLink from "@/components/shared/MapLink";
import SearchStyledSelect from "@/components/shared/SearchStyledSelect";
import { useAppData } from "@/lib/providers/AppDataProvider";

import VendorTags from "@/components/market/management/VendorTags";

const EventParticipantGroupVendorList = ({
  name,
  event,
  eventParticipantGroup,
  participants,
  buttonRef
}) => {
  const api = useApi();
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });

  const [loaded, setLoaded] = useState(false);
  const [participantState, setParticipantState] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const [showMap, setShowMap] = useState(false);

  const [spaceMap, setSpaceMap] = useState(null);
  const [selectedSpacePlanSpace, setSelectedSpacePlanSpace] = useState(null);
  const [spacePlanJsonForDate, setSpacePlanJsonForDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [firstDate, setFirstDate] = useState(null);
  const [nextDate, setNextDate] = useState(null);
  const [mapPopup, setMapPopup] = useState(null);

  const { flattenedVendorTypes: vendorTypesFlattened } = useAppData();

  useEffect(() => {
    if (eventParticipantGroup && participants) {
      // check if the type is VENDOR and based on that, check the scheduleConfiguration to see if you need to load a spaceMap and spacePlan
      if (eventParticipantGroup.type === "MARKET") {
        api
          .market_getVendorScheduleMap(name, event.managedEventReferenceId)
          .then(schedule => {
            setSpaceMap(schedule.spaceMap);
            let dates = Object.keys(schedule.spaceMap);
            setAvailableDates(dates);
            let today = moment().format("YYYY/MM/DD");
            if (dates.includes(today)) {
              setFirstDate(today);
              setSelectedDate(today);
              setNextDate(nextAvailableDate(today, dates));
            } else {
              let nextDate = nextAvailableDate(today, dates);
              if (nextDate) {
                setFirstDate(nextDate);
                setSelectedDate(nextDate);
                let dateAfter = nextAvailableDate(nextDate, dates);
                if (dateAfter) {
                  setNextDate(dateAfter);
                }
              }
            }
          });
        setParticipantState(participants);
        setLoaded(true);
      } else {
        setParticipantState(participants);
        setLoaded(true);
      }
    }
  }, [eventParticipantGroup, participants]);

  const nextAvailableDate = (date, availableDates) => {
    // find the next closest date
    return availableDates.find(d => moment(d).isAfter(moment(date)));
  };

  useEffect(() => {
    if (selectedDate && eventParticipantGroup.type === "MARKET") {
      api
        .market_getVendorScheduleMapForDate(
          name,
          event.managedEventReferenceId,
          selectedDate
        )
        .then(scheduleMap => {
          setSpacePlanJsonForDate(scheduleMap);
        });
    }
  }, [selectedDate]);

  // useEffect(() => {
  //   if (selectedDate && spaceMap && spacePlanJson) {
  //     let dateSpacePlan = { ...spacePlanJson };
  //     let spaceList = spaceMap[selectedDate];
  //     // look up the features in dateSpacePlan and update the backgroundColor property to red if occupied, or green if not
  //     let updatedFeatures = dateSpacePlan.features
  //       .map(feature => {
  //         let space = spaceList.find(
  //           space => space.spaceId === feature.id && space.occupantId !== null
  //         );
  //         if (space) {
  //           return {
  //             ...feature,
  //             properties: {
  //               ...feature.properties,
  //               backgroundColor: "#263239"
  //             }
  //           };
  //         } else {
  //           return {
  //             ...feature,
  //             properties: {
  //               ...feature.properties,
  //               backgroundColor: "#CCCCCC",
  //               borderColor: "#CCCCCC"
  //             }
  //           };
  //         }
  //       })
  //       .filter(f => f != null);
  //     let occupantIds = spaceList
  //       .map(space => space.occupantId)
  //       .filter(id => id !== null);
  //     let participantsForDate = participants.filter(
  //       p => p.profile && occupantIds.includes(p.profile.id)
  //     );
  //     setParticipantState(participantsForDate);
  //     setSpacePlanJsonForDate({ ...dateSpacePlan, features: updatedFeatures });
  //   }
  // }, [selectedDate, spacePlanJson, spaceMap, participants]);

  const ParticipantPopup = ({ lat, lng, space }) => {
    return (
      <MapBoxMapPopup longitude={lng} latitude={lat} anchor="bottom">
        {buildParticipantContent(
          space.occupantId,
          participants.find(p => p.profile.id === space.occupantId),
          false,
          true
        )}
        <button
          type="button"
          className="a"
          onClick={() => setSelectedParticipant(space.occupantId)}
        >
          More Info..
        </button>
      </MapBoxMapPopup>
    );
  };

  const handleSpacePlanSpaceClicked = (evt, props) => {
    let spaceList = spaceMap[selectedDate];
    if (props) {
      let space = spaceList.find(space => space.spaceId === props.id);
      setSelectedSpacePlanSpace(space && space.occupantId ? props.id : null); // this highlights the item on the map
      if (space && space.occupantId) {
        let popup = (
          <ParticipantPopup
            lat={evt.lngLat.lat}
            lng={evt.lngLat.lng}
            space={space}
          />
        );
        setMapPopup(popup);
      }
    } else {
      setSelectedSpacePlanSpace(null);
      setMapPopup(null);
      setSelectedParticipant(null);
    }
  };

  const searchChanged = selectedOption => {
    if (selectedOption != null) {
      let spaceList = spaceMap[selectedDate];
      if (spaceList) {
        let space = spaceList.find(
          space => space.occupantId === selectedOption
        );
        setSelectedSpacePlanSpace(space?.spaceId);
        if (space && space.occupantId) {
          let ft = spacePlanJsonForDate.features.find(
            feature => feature.properties.occupantId === space.occupantId
          );
          if (ft && ft.geometry.type === "MultiPolygon") {
            // Find center point of polygon coordinates
            let coords = ft.geometry.coordinates[0][0]; // Get outer ring coordinates
            let centerY =
              coords.reduce((sum, point) => sum + point[0], 0) / coords.length;
            let centerX =
              coords.reduce((sum, point) => sum + point[1], 0) / coords.length;
            let popup = (
              <ParticipantPopup lat={centerX} lng={centerY} space={space} />
            );
            setMapPopup(popup);
          }
        }
      }
    } else {
      setSelectedSpacePlanSpace(null);
    }
  };

  const formatDate = date => {
    return new DateObject({ date: date, format: "YYYY/MM/DD" }).format(
      "MMMM D, YYYY"
    );
  };

  const buildParticipantContent = (
    key,
    participant,
    showSelected = true,
    condensed = false
  ) => {
    return (
      <div
        className={`participant-content${
          condensed ? " condensed" : " default-card-wrapper"
        }${
          showSelected
            ? selectedParticipant === participant.profile.id
              ? " selected"
              : ""
            : ""
        }`}
        id={`anchor-${participant.profile?.id}`}
      >
        <div className="participant-avatar">
          {participant.profile && participant.profile.coverPhotoId && (
            <Ratio aspectRatio="16x9">
              <UralityImage
                name={name}
                imageId={participant.profile.coverPhotoId}
                id={`${participant.id}-cover-photo`}
                key={`${participant.id}-cover-photo`}
              />
            </Ratio>
          )}
        </div>
        <div className="default-card-wrapper-content">
          <div className="embedded-content-section vertical">
            <div className="participant-text" style={{ marginTop: "1rem" }}>
              <h4>{participant.profile.displayName}</h4>
              {!condensed && (
                <>
                  {participant.profile.descriptionRichText && (
                    <RichTextBlock
                      richText={participant.profile.descriptionRichText}
                      collapsable={true}
                      as="span"
                    />
                  )}
                </>
              )}
            </div>
            {participant.notesRichText && (
              <div className="participant-text">
                <RichTextBlock
                  richText={participant.notesRichText}
                  collapsable={false}
                  as="span"
                />
              </div>
            )}
            {participant.profile?.bio && !condensed && (
              <>
                {participant.profile.bio.about && (
                  <div
                    className="participant-text"
                    style={{ marginTop: "1rem" }}
                  >
                    <h6>ABOUT</h6>
                    <CollapseText
                      text={participant.profile.bio.about}
                      maxLength={200}
                    />
                  </div>
                )}
              </>
            )}
            {(participant.profile?.vendorMacroType ||
              participant.profile?.products) && (
              <VendorTags
                vendor={{
                  vendorProfile: {
                    macroType: participant.profile.vendorMacroType,
                    products: participant.profile.products
                  }
                }}
                vendorTypesFlattened={vendorTypesFlattened}
              />
            )}
          </div>
          {!condensed &&
            participant.profile?.socialProfile &&
            participant.profile.socialProfile.length > 0 && (
              <div className="embedded-content-section vertical">
                <SocialLinks
                  socialProfile={participant.profile.socialProfile}
                  condensed={false}
                />
              </div>
            )}
        </div>
      </div>
    );
  };

  const DateButtons = ({ selectedDate, firstDate, nextDate }) => {
    let isFirstDateToday = moment().format("YYYY/MM/DD") === firstDate;
    let isFirstDateTomorrow = moment().format("YYYY/MM/DD") === firstDate;
    let isNextDateTomorrow =
      moment()
        .add(1, "days")
        .format("YYYY/MM/DD") === nextDate;

    let firstDateDOW = moment(firstDate).format("dddd");
    let nextDateDOW = nextDate ? moment(nextDate).format("dddd") : null;

    const firstDayLabel = () => {
      if (isFirstDateToday) {
        return "Today";
      } else if (isFirstDateTomorrow) {
        return "Tomorrow";
      } else if (isWithin7Days(firstDate)) {
        return firstDateDOW;
      } else {
        return "Upcoming";
      }
    };

    const nextDayLabel = () => {
      if (nextDate) {
        if (isNextDateTomorrow) {
          return "Tomorrow";
        } else if (isWithin7Days(nextDate)) {
          return nextDateDOW;
        } else {
          return "Upcoming";
        }
      } else {
        return "";
      }
    };

    const isWithin7Days = dateString => {
      // Parse the input date string
      const inputDate = new Date(dateString);

      // Get the current date
      const currentDate = new Date();

      // Calculate the time difference in milliseconds
      const timeDiff = Math.abs(inputDate - currentDate);

      // Convert milliseconds to days
      const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

      // Check if the input date is within 7 days of the current date
      return daysDiff <= 7;
    };

    return (
      <div className={"date-buttons"}>
        <button
          type="button"
          className={`date-button${
            firstDate === selectedDate ? " selected" : ""
          }`}
          onClick={() => {
            setSelectedDate(firstDate);
            setSelectedSpacePlanSpace(null);
          }}
        >
          <h5>{firstDayLabel()}</h5>
          <span>{formatDate(firstDate)}</span>
        </button>
        {nextDate && (
          <button
            type="button"
            className={`date-button${
              nextDate === selectedDate ? " selected" : ""
            }`}
            onClick={() => {
              setSelectedDate(nextDate);
              setSelectedSpacePlanSpace(null);
            }}
          >
            <h5>{nextDayLabel()}</h5>
            <span>{formatDate(nextDate)}</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {isSm && spaceMap && spacePlanJsonForDate && !showMap && buttonRef && (
        <>
          {ReactDOM.createPortal(
            <>
              <button
                className="btn btn-secondary with-icon"
                onClick={() => {
                  setShowMap(true);
                }}
              >
                <span className="material-icons">{"map"}</span>
                <span>View Map</span>
              </button>
            </>,
            buttonRef.current
          )}
        </>
      )}
      {isSm && spaceMap && spacePlanJsonForDate && (
        <FullscreenMapAndDrawer
          show={showMap}
          mapComponent={
            <>
              <MapBoxMapWrapper
                addMyLocation={true}
                height="100dvh"
                width="100vw"
                numbered={false}
                locations={[]}
                routes={[]}
                selectedRoute={null}
                layers={[]}
                selectedLayers={[]}
                selectedLocation={null}
                onLocationClicked={null}
                onFeatureClicked={null}
                onMapClicked={() => {
                  handleSpacePlanSpaceClicked(null, null);
                }}
                spacePlans={[spacePlanJsonForDate]}
                selectedSpacePlanSpace={selectedSpacePlanSpace}
                onSpacePlanSpaceClicked={(evt, props) => {
                  // console.log("onSpacePlanSpaceClicked", props);
                  handleSpacePlanSpaceClicked(evt, props);
                }}
                mapToggle={{
                  pins: true,
                  regions: true,
                  routes: true,
                  waypoints: true,
                  assetGroups: true,
                  features: true,
                  spacePlan: true
                }}
                fixedSettings={{
                  boundingBox: [
                    [
                      spacePlanJsonForDate.bbox[0],
                      spacePlanJsonForDate.bbox[1]
                    ],
                    [spacePlanJsonForDate.bbox[2], spacePlanJsonForDate.bbox[3]]
                  ],
                  minZoom: 18,
                  maxZoom: 24
                }}
                popupObject={mapPopup}
              />
              <div className={"search"}>
                <SearchStyledSelect
                  options={participantState.map(p => {
                    return {
                      value: p.profile.id,
                      label: p.profile.displayName
                    };
                  })}
                  isClearable={true}
                  isSearchable={true}
                  placeholder={"Find a vendor.."}
                  onChange={selectedOption => {
                    searchChanged(selectedOption?.value);
                  }}
                />
              </div>
            </>
          }
          drawerComponent={
            selectedParticipant ? (
              <>
                {buildParticipantContent(
                  0,
                  participantState.find(
                    p => p.profile.id === selectedParticipant
                  ),
                  false
                )}
              </>
            ) : null
          }
          closeComponent={
            <button
              type="button"
              className={"btn btn-secondary with-icon"}
              onClick={() => {
                setShowMap(false);
              }}
            >
              <span className="material-icons">list</span>
              <span>View List</span>
            </button>
          }
          onDrawerClose={() => {
            setSelectedSpacePlanSpace(null);
            setSelectedParticipant(null);
          }}
        />
      )}
      <div style={{ marginBottom: "2rem" }} />
      <div className="container">
        {loaded && eventParticipantGroup.type === "VENDOR" && firstDate && (
          <div className="vendor-header">
            {firstDate && (
              <DateButtons
                selectedDate={selectedDate}
                firstDate={firstDate}
                nextDate={nextDate}
              />
            )}
            <div className={"flex-grow"} />
          </div>
        )}
        <div className="event-participant-group-participant-list">
          {loaded && participantState && participantState.length > 0 && (
            <>
              {participantState.map((participant, key) => {
                return buildParticipantContent(key, participant);
              })}
            </>
          )}
          {loaded && participantState && participantState.length === 0 && (
            <p>Nothing to show here.</p>
          )}
          {!loaded && <LoadingAnimation />}
        </div>
      </div>
    </>
  );
};
export default EventParticipantGroupVendorList;
